import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Message as SlackIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../../services/api';

// eslint-disable-next-line max-lines-per-function
function SlackWorkspaceConnector({ entityId, onConnectionChange, selectedWorkspace, onWorkspaceSelect }) {
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [error, setError] = useState(null);
  const [authUrl, setAuthUrl] = useState(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchConnectedWorkspaces();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const fetchConnectedWorkspaces = async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);
    try {
      // Check for existing Slack connections for this entity
      const response = await api.listSlackWorkspaces(entityId);
      setWorkspaces(response.workspaces || []);
    } catch (err) {
      console.error('Failed to fetch Slack workspaces:', err);
      setError('Failed to load Slack workspace connections');
    } finally {
      setLoading(false);
    }
  };

  const initializeSlackAuth = async () => {
    if (!entityId) {
      setError('Please select an entity first');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.initializeSlackAuth(entityId);

      if (response.authUrl) {
        setAuthUrl(response.authUrl);
        setAuthDialogOpen(true);
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (err) {
      console.error('Failed to initialize Slack auth:', err);
      setError('Failed to initialize Slack authorization: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthComplete = () => {
    setAuthDialogOpen(false);
    setAuthUrl(null);
    // Wait a moment for the OAuth callback to complete
    setTimeout(() => {
      fetchConnectedWorkspaces();
      if (onConnectionChange) {
        onConnectionChange();
      }
    }, 2000);
  };

  const refreshWorkspaceStatus = async (workspaceId) => {
    setRefreshing(true);
    try {
      const response = await api.refreshSlackWorkspace(entityId, workspaceId);

      // Update the workspace in the list
      setWorkspaces(prev => prev.map(ws =>
        ws.id === workspaceId ? { ...ws, ...response.workspace } : ws
      ));
    } catch (err) {
      console.error('Failed to refresh workspace status:', err);
      setError('Failed to refresh workspace status');
    } finally {
      setRefreshing(false);
    }
  };

  const disconnectWorkspace = async (workspaceId) => {
    if (!window.confirm('Are you sure you want to disconnect this Slack workspace? All related ingestion points will be disabled.')) {
      return;
    }

    try {
      await api.disconnectSlackWorkspace(entityId, workspaceId);

      // Remove from list
      setWorkspaces(prev => prev.filter(ws => ws.id !== workspaceId));

      if (selectedWorkspace?.id === workspaceId && onWorkspaceSelect) {
        onWorkspaceSelect(null);
      }

      if (onConnectionChange) {
        onConnectionChange();
      }
    } catch (err) {
      console.error('Failed to disconnect workspace:', err);
      setError('Failed to disconnect workspace: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'error': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckIcon />;
      case 'error': return <ErrorIcon />;
      case 'pending': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <SlackIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Slack Workspace Connections
        </Typography>
        <Button
          variant="contained"
          onClick={initializeSlackAuth}
          disabled={loading || !entityId}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SlackIcon />}
        >
          Connect Workspace
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!entityId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select an entity to manage Slack workspace connections.
        </Alert>
      )}

      {loading && workspaces.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : workspaces.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <SlackIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Slack Workspaces Connected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect a Slack workspace to start ingesting messages and files from channels.
            </Typography>
            <Button
              variant="outlined"
              onClick={initializeSlackAuth}
              disabled={loading || !entityId}
              startIcon={<SlackIcon />}
            >
              Connect Your First Workspace
            </Button>
          </CardContent>
        </Card>
      ) : (
        <List>
          {workspaces.map((workspace) => (
            <Card key={workspace.id} sx={{ mb: 2 }}>
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: selectedWorkspace?.id === workspace.id ? 'action.selected' : 'transparent'
                }}
                onClick={() => onWorkspaceSelect && onWorkspaceSelect(workspace)}
              >
                <ListItemIcon>
                  <SlackIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {workspace.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={workspace.status}
                        color={getStatusColor(workspace.status)}
                        icon={getStatusIcon(workspace.status)}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Team ID: {workspace.teamId}
                      </Typography>
                      {workspace.lastSync && (
                        <Typography variant="caption" color="text.secondary">
                          Last sync: {new Date(workspace.lastSync).toLocaleString()}
                        </Typography>
                      )}
                      {workspace.error && (
                        <Typography variant="caption" color="error">
                          Error: {workspace.error}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Refresh status">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshWorkspaceStatus(workspace.id);
                      }}
                      disabled={refreshing}
                      size="small"
                    >
                      {refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Disconnect workspace">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        disconnectWorkspace(workspace.id);
                      }}
                      color="error"
                      size="small"
                    >
                      <ErrorIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            </Card>
          ))}
        </List>
      )}

      {/* Slack OAuth Dialog */}
      <Dialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SlackIcon color="primary" />
            Connect Slack Workspace
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You'll be redirected to Slack to authorize FAANA Growth OS to access your workspace.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The app will request permission to:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Read messages from selected channels" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Access user and team information" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Download files shared in channels" />
            </ListItem>
          </List>
          {authUrl && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<OpenInNewIcon />}
                onClick={() => {
                  window.open(authUrl, '_blank', 'width=600,height=700');
                }}
              >
                Open Slack Authorization
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAuthComplete} variant="contained">
            I've Completed Authorization
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SlackWorkspaceConnector;